<template>
  <form
    class="space-y-4"
    aria-labelledby="form-title"
    novalidate
    @submit.prevent="submitForm"
  >
    <div v-for="field in fields" :key="field.name">
      <component
        :is="field.component"
        :field="field"
        :rules="field.rules"
      />
    </div>

    <div class="pt-4">
      <TurnstileWidget
        v-if="turnstileSiteKey"
        :sitekey="turnstileSiteKey"
        @token="handleTurnstileToken"
        @error="handleTurnstileError"
        @expired="handleTurnstileExpired"
      />
    </div>

    <button
      type="submit"
      class="w-full flex items-center justify-center rounded bg-black px-4 py-3 text-white font-semibold transition-all hover:bg-koshee-orange disabled:cursor-not-allowed disabled:bg-gray-400 focus:outline-none focus:ring-0"
      :disabled="submitting || (turnstileSiteKey && !turnstileToken)"
      :aria-busy="submitting"
    >
      <span v-if="submitting" aria-live="polite">Submitting…</span>
      <span v-else-if="turnstileSiteKey && !turnstileToken">Complete CAPTCHA to submit</span>
      <span v-else>Submit Ticket</span>
    </button>
  </form>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";
import { useForm } from "vee-validate";
import { fromSchema } from "../validation/schema";
import { useFieldRegistry } from "../composables/useFieldRegistry";
import TurnstileWidget from "./TurnstileWidget.vue";
import type { FormError } from "@hubspot-ticket/shared";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const turnstileToken = ref<string | undefined>();

const props = defineProps<{
  schema: unknown;
  submitting: boolean;
  backendErrors?: FormError[];
}>();

const emit = defineEmits<{
  (event: "submit", payload: unknown): void;
}>();

const handleTurnstileToken = (token: string) => {
  turnstileToken.value = token;
};

const handleTurnstileError = () => {
  turnstileToken.value = undefined;
};

const handleTurnstileExpired = () => {
  turnstileToken.value = undefined;
};

const registry = useFieldRegistry();
const parsedSchema = computed(() => fromSchema(props.schema, registry.resolveComponent));

const formOptions: Record<string, unknown> = {
  initialValues: parsedSchema.value.initialValues
};

const { handleSubmit, resetForm, setErrors } = useForm(formOptions);

const fields = computed(() => parsedSchema.value.fields);

watch(
  parsedSchema,
  (schema) => {
    resetForm({ values: schema.initialValues });
  },
  { immediate: true }
);

// Watch for backend errors and set them in the form
watch(
  () => props.backendErrors,
  (errors) => {
    if (errors && errors.length > 0) {
      const errorMap: Record<string, string> = {};
      errors.forEach((error) => {
        errorMap[error.field] = error.detail || `Field error: ${error.code}`;
      });
      setErrors(errorMap);
    }
  }
);

const submitForm = handleSubmit(async (submittedValues) => {
  emit("submit", { ...submittedValues, turnstileToken: turnstileToken.value });
});
</script>
